// Общий error-handling для всех hooks/screens. Source of truth: docs/specs/sprint-3/stage-4-screens.md §2.3.
// Backend error shape: { message: string, errors?: Record<string,string[]> } — Laravel-формат, поле errors (не validation_errors).

import axios, { type AxiosError } from 'axios';
import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import type { ApiError, UploadApiError, UploadFailureReason } from './types';

export function isApiError(err: unknown): err is AxiosError<ApiError> {
  return axios.isAxiosError(err);
}

export function getApiMessage(err: unknown, fallback = 'Сервер недоступен, попробуйте ещё раз'): string {
  if (isApiError(err)) {
    return err.response?.data?.message ?? fallback;
  }
  return fallback;
}

export function getValidationErrors(err: unknown): Record<string, string[]> | null {
  if (isApiError(err) && err.response?.data?.errors) {
    return err.response.data.errors;
  }
  return null;
}

// Кладёт 422-ошибки в react-hook-form через setError.
//
// Без `fieldMap`: применяются ТОЛЬКО плоские backend-ключи (`email`, `password`).
// Вложенные (`step.free_text`, `flags.pd_medical`) тихо игнорируются — без map'а
// мы не знаем, как они называются в форме.
//
// С `fieldMap`: маппинг backend-key → form-field-name. Только присутствующие в
// карте ключи попадают в setError; остальные пропускаются (allow-list). Используй
// для survey-шагов (`'step.free_text': 'free_text'`) и consent
// (`'flags.pd_medical': 'pd_medical'`).
//
// Возвращает true если хоть одно сообщение было применено к форме — screen может
// использовать это как сигнал «422 уже обработан, тост не нужен».
export function applyValidationErrors<TFields extends string>(
  err: unknown,
  setError: (field: TFields, error: { type: 'server'; message: string }) => void,
  fieldMap?: Partial<Record<string, TFields>>,
): boolean {
  const errors = getValidationErrors(err);
  if (!errors) return false;
  let applied = false;
  for (const [field, messages] of Object.entries(errors)) {
    const message = messages?.[0];
    if (!message) continue;
    if (fieldMap) {
      const target = fieldMap[field];
      if (!target) continue;
      setError(target, { type: 'server', message });
      applied = true;
    } else {
      if (field.includes('.')) continue; // вложенный ключ без явного map'а — skip
      setError(field as TFields, { type: 'server', message });
      applied = true;
    }
  }
  return applied;
}

export function useApiErrorToast(): (err: unknown) => void {
  const { toast } = useToast();
  return useCallback(
    (err: unknown) => {
      if (!isApiError(err)) {
        toast({
          title: 'Ошибка',
          description: 'Неизвестная ошибка, попробуйте ещё раз',
          variant: 'destructive',
          dedupeKey: 'api:unknown',
        });
        return;
      }
      const status = err.response?.status;
      if (status === 429) {
        toast({
          title: 'Слишком много попыток',
          description: 'Попробуйте через минуту',
          variant: 'destructive',
          dedupeKey: 'api:throttled',
        });
        return;
      }
      if (status && status >= 500) {
        toast({
          title: 'Ошибка сервера',
          description: 'Сервер недоступен, попробуйте ещё раз',
          variant: 'destructive',
          dedupeKey: 'api:server_error',
        });
        return;
      }
      if (!err.response) {
        toast({
          title: 'Нет соединения',
          description: 'Проверьте интернет и попробуйте ещё раз',
          variant: 'warning',
          // Состояние сохраняется и после прочтения — само не уходит.
          persistent: true,
          dedupeKey: 'api:offline',
        });
        return;
      }
      toast({
        title: 'Ошибка',
        description: getApiMessage(err),
        variant: 'destructive',
        dedupeKey: 'api:generic',
      });
    },
    [toast],
  );
}

// ─── Sprint 4 upload error mapping ────────────────────────────────────────
// Source of truth: docs/specs/sprint-4/stage-4-frontend.md §6.3. Pure data-mappers
// живут здесь; React adapter hooks — в api/error-toast.tsx (split: TS vs TSX).

export type UploadErrorToast = {
  kind:
    | 'corrupted'
    | 'not_a_lab_report'
    | 'needs_ocr'
    | 'unsupported_analysis_type'
    | 'parser_unavailable'
    | 'duplicate_file'
    | 'throttle'
    | 'generic'
    | 'invalid_status_transition'; // Sprint 5 A-S5-5 reopen wrong-status
  title: string;
  description: string;
  action?: { label: string; href: string };
};

/**
 * HEAL-29: три РАЗНЫЕ причины отказа раньше показывались одним текстом
 * «Не похоже на анализ крови». На проде это выглядело так: 8 отказов из 14 —
 * это «файл не удалось прочитать» (PDF-скан без текстового слоя), а человек
 * читал, что его анализ крови — не анализ крови, и загружал тот же файл снова.
 *
 * Разводим по смыслу: проблема с файлом / это не лабораторный документ /
 * исследование мы пока не поддерживаем. У каждой — свой следующий шаг.
 */
export function mapPollingFailure(reason: UploadFailureReason): UploadErrorToast {
  switch (reason) {
    case 'corrupted':
      return { kind: 'corrupted', title: 'Файл повреждён', description: 'Попробуйте загрузить заново.' };
    case 'needs_ocr_review':
      return {
        kind: 'needs_ocr',
        title: 'Не удалось прочитать файл',
        description: 'Похоже, это скан или фото без текста. Попробуйте PDF из лаборатории или снимок покрупнее.',
      };
    case 'unsupported_analysis_type':
      return {
        kind: 'unsupported_analysis_type',
        // HEAL-29: моча поддерживается с 14.08.2026, остался биоимпеданс.
        title: 'Такое исследование пока не распознаём',
        description: 'Мы читаем анализы крови и мочи. Другие типы добавим позже.',
      };
    case 'not_a_lab_report':
      return {
        kind: 'not_a_lab_report',
        title: 'Не нашли результатов анализов',
        description: 'Проверьте, что в файле есть таблица с показателями.',
      };
    case 'parser_unavailable':
    case 'parser_response_invalid':
    case 'openai_error_terminal':
      return {
        kind: 'parser_unavailable',
        title: 'Сервис распознавания недоступен',
        description: 'Попробуйте через несколько минут.',
      };
    default:
      return { kind: 'generic', title: 'Ошибка', description: 'Что-то пошло не так. Попробуйте ещё раз.' };
  }
}

export function mapUploadError(error: AxiosError<UploadApiError>): UploadErrorToast {
  const status = error.response?.status;
  const body = error.response?.data;
  // Sprint 5 A-S5-5: 409 invalid_status_transition (reopen на не-reported анализе).
  if (status === 409 && body?.error === 'invalid_status_transition') {
    return {
      kind: 'invalid_status_transition',
      title: 'Анализ нельзя повторно открыть',
      description: 'Статус анализа изменился. Обновите страницу.',
    };
  }
  if (status === 409 && body?.error === 'duplicate_file') {
    return {
      kind: 'duplicate_file',
      title: 'Уже загружено',
      description: 'Этот файл уже есть в вашей истории.',
      action: { label: 'Открыть историю', href: '/upload/history' },
    };
  }
  // HEAL-46a D-12: ветки `openai_daily_limit_per_user_reached` здесь больше нет.
  // Дневной лимит перестал быть причиной отказа в загрузке — он ограничивает
  // распознавание, а не приём. Файл сверх лимита принимается (202) и ждёт своей
  // очереди; сообщить об этом — задача экрана загрузки, а не обработчика ошибок.
  if (status === 429) {
    return { kind: 'throttle', title: 'Слишком много загрузок', description: 'Попробуйте позже.' };
  }
  if (status === 422 && body?.failure_reason) {
    return mapPollingFailure(body.failure_reason);
  }
  return {
    kind: 'generic',
    title: 'Ошибка',
    description: getApiMessage(error, 'Что-то пошло не так. Попробуйте ещё раз.'),
  };
}
